import { useGetAccountsQuery } from "@/modules/account/services";
import { ResidenceType } from "@/modules/transaction/common/enum";
import type { IBudgetTransferForm } from "@/modules/transaction/common/types";
import { BudgetTransferForm } from "@/modules/transaction/components";
import { budgetTransferValidationSchema } from "@/modules/transaction/validations/validationSchema";
import { DATE_FORMAT, INITIAL_PAGE_SIZE } from "@/shared/common/constants";
import {
  CloudflareAsset,
  Currencies,
  SideSheetPosition,
  StaticUrl,
  Storage
} from "@/shared/common/enums";
import type { IOperationPageProps } from "@/shared/common/types";
import { CloudFlareImage, InfoLinks, PageHeader } from "@/shared/components";
import { formatDate } from "@/shared/helpers";
import { useMediaQuery, useTranslationExists } from "@/shared/hooks";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { FormContainer } from "@ab.uitools/ui-library/components/FormContainer";
import { SideSheet } from "@ab.uitools/ui-library/components/SideSheet";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useState } from "react";

export const BudgetTransfer = ({ isCopy, isEdit }: IOperationPageProps) => {
  const isDesktop = useMediaQuery();
  const { t, exists } = useTranslationExists();
  const [isSideBarOpen, setSideBarOpen] = useState<boolean>(false);
  const [isTinValid, setIsTinValid] = useState<boolean>(true);
  const [isAccountNumberInvalid, setIsAccountNumberInvalid] = useState<boolean>(false);
  const [isNameUnique, setIsNameUnique] = useState<boolean>(false);
  const { data: accountsList = [] } = useGetAccountsQuery({
    pageSize: INITIAL_PAGE_SIZE,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const initialValues: IBudgetTransferForm = {
    transactionNumber: "",
    creditBudgetAccount: "",
    debitAccount: "",
    scheduleDate: formatDate(new Date(), DATE_FORMAT.YYYY_MM_DD),
    amount: "",
    tin: localStorage.getItem(Storage.Tin) || "",
    purpose: "",
    isUrgent: false,
    commissionAccount: "",
    isAuthorizedTransfer: false,
    currency: Currencies.AMD,
    isSaveAsTemplate: false,
    templateName: "",
    authorizedBudgetTransferParty: {
      fullName: "",
      residency: ResidenceType.Resident,
      partyType: "",
      tin: "",
      ssn: "",
      documentType: "",
      documentNumber: "",
      address: "",
      authorizedPerson: "",
      additionalInfo: "",
    },
    regionCode: "",
    messages: {
      reportCode: "",
      message1: "",
      message2: "",
      message3: "",
      message4: "",
      message5: "",
      message6: "",
    }
  };

  return (
    <>
      <FormContainer
        className="full-width page-container form-layout"
        formId="form-container"
        mode="onBlur"
        reValidateMode="onBlur"
        initialValues={initialValues}
        validationScheme={budgetTransferValidationSchema({
          t,
          isTinValid,
          accountsList,
          isAccountNumberInvalid,
          isNameUnique,
        })}
      >
        <PageHeader>
          {!isSideBarOpen && exists("transfer.sidebarTitle") && (
            <Button
              type="secondary"
              size="small"
              className="side-sheet-btn"
              data-testid="open-info-side-sheet"
              data-ga-id="open-info-side-sheet"
              data-ga-action="Open Budget transfer info side sheet"
              iconProps={{
                Component: (props: ISVGIconProps) => (
                  <CloudFlareImage {...props} path={CloudflareAsset.Info}/>
                ),
                alignment: "left",
              }}
              onClick={() => setSideBarOpen(!isSideBarOpen)}
            />
          )}
        </PageHeader>
        <BudgetTransferForm
          isEdit={isEdit}
          isCopy={isCopy}
          accountsList={accountsList}
          setIsTinValid={setIsTinValid}
          setIsAccountNumberInvalid={setIsAccountNumberInvalid}
          isNameUnique={isNameUnique}
          setIsNameUnique={setIsNameUnique}
        />
      </FormContainer>
      <SideSheet
        position={isDesktop ? SideSheetPosition.Right : SideSheetPosition.Bottom}
        isPositioned={!isDesktop}
        withOverlay={!isDesktop}
        isOpen={isSideBarOpen}
        onClose={() => setSideBarOpen(false)}
        title={t("transfer.sidebarTitle")}
      >
        <div className="flexbox flex-wrap--wrap">
          <Text type="primary" className="mb-10">
            {t("transfer.budget.sidebarMessage")}
          </Text>
          <InfoLinks keys={[StaticUrl.CorporateTariffs, StaticUrl.CardTariffs]}/>
        </div>
      </SideSheet>
    </>
  );
};
